import React, { useState, useEffect } from "react";
import {
  Card, CardHeader, CardBody, CardTitle,
  Row, Col, Table,
} from "reactstrap";
import { AlumnoService } from "service/AlumnoService";
import { DocenteService } from "service/DocenteService";
import { CursoService } from "service/CursoService";
import { CicloService } from "service/CicloService";

const alumnoService = new AlumnoService();
const docenteService = new DocenteService();
const cursoService = new CursoService();
const cicloService = new CicloService();

function Dashboard() {
  const [cicloActual, setCicloActual] = useState(null);
  const [stats, setStats] = useState({
    alumnos: 0,
    docentes: 0,
    cursos: 0,
    docentesActivos: 0,
  });
  const [ultimosAlumnos, setUltimosAlumnos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [ciclosData, docentesData, cursosData] = await Promise.all([
        cicloService.getCiclos().catch(() => []),
        docenteService.getDocentes().catch(() => []),
        cursoService.getCursos().catch(() => []),
      ]);

      const ciclos = Array.isArray(ciclosData) ? ciclosData : ciclosData?.data || [];
      const docentes = Array.isArray(docentesData) ? docentesData : docentesData?.data || [];
      const cursos = Array.isArray(cursosData) ? cursosData : cursosData?.data || [];

      // Tomar el ciclo más reciente como ciclo actual
      const ciclo = ciclos[ciclos.length - 1] || null;
      setCicloActual(ciclo);

      const docentesActivos = docentes.filter((d) => d.estado === "1").length;

      // Cargar alumnos del ciclo actual
      let alumnos = [];
      if (ciclo) {
        const alumnosData = await alumnoService
          .getAlumnosByCiclo({ ciclo: ciclo._id })
          .catch(() => []);
        alumnos = Array.isArray(alumnosData) ? alumnosData : alumnosData?.data || [];
      }

      setStats({
        alumnos: alumnos.length,
        docentes: docentes.length,
        cursos: cursos.length,
        docentesActivos,
      });

      // Últimos 5 alumnos matriculados
      setUltimosAlumnos(alumnos.slice(0, 5));
    } catch (error) {
      console.error("Error cargando datos del dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content">
      {/* Ciclo actual */}
      {cicloActual && (
        <Row className="mb-2">
          <Col md="12">
            <p className="text-muted mb-1">
              Ciclo actual:{" "}
              <strong className="text-white">{cicloActual.denominacion}</strong>
            </p>
          </Col>
        </Row>
      )}

      {/* Tarjetas de métricas */}
      <Row>
        <Col lg="3" md="6">
          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col xs="5">
                  <div className="info-icon text-center icon-primary">
                    <i className="tim-icons icon-single-02" />
                  </div>
                </Col>
                <Col xs="7">
                  <div className="numbers">
                    <p className="card-category">Alumnos</p>
                    <CardTitle tag="h3">
                      {loading ? "..." : stats.alumnos}
                    </CardTitle>
                  </div>
                </Col>
              </Row>
            </CardBody>
            <div className="card-footer">
              <hr />
              <div className="stats">
                <i className="tim-icons icon-refresh-01" /> Matriculados en el ciclo
              </div>
            </div>
          </Card>
        </Col>

        <Col lg="3" md="6">
          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col xs="5">
                  <div className="info-icon text-center icon-success">
                    <i className="tim-icons icon-badge" />
                  </div>
                </Col>
                <Col xs="7">
                  <div className="numbers">
                    <p className="card-category">Docentes</p>
                    <CardTitle tag="h3">
                      {loading ? "..." : stats.docentesActivos}
                      <small className="text-muted" style={{ fontSize: "0.5em" }}>
                        /{stats.docentes}
                      </small>
                    </CardTitle>
                  </div>
                </Col>
              </Row>
            </CardBody>
            <div className="card-footer">
              <hr />
              <div className="stats">
                <i className="tim-icons icon-check-2" /> Activos / Total
              </div>
            </div>
          </Card>
        </Col>

        <Col lg="3" md="6">
          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col xs="5">
                  <div className="info-icon text-center icon-warning">
                    <i className="tim-icons icon-book-bookmark" />
                  </div>
                </Col>
                <Col xs="7">
                  <div className="numbers">
                    <p className="card-category">Cursos</p>
                    <CardTitle tag="h3">
                      {loading ? "..." : stats.cursos}
                    </CardTitle>
                  </div>
                </Col>
              </Row>
            </CardBody>
            <div className="card-footer">
              <hr />
              <div className="stats">
                <i className="tim-icons icon-book-bookmark" /> Total de cursos
              </div>
            </div>
          </Card>
        </Col>

        <Col lg="3" md="6">
          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col xs="5">
                  <div className="info-icon text-center icon-danger">
                    <i className="tim-icons icon-chart-pie-36" />
                  </div>
                </Col>
                <Col xs="7">
                  <div className="numbers">
                    <p className="card-category">Ciclo</p>
                    <CardTitle tag="h3" style={{ fontSize: "1rem" }}>
                      {loading ? "..." : (cicloActual?.denominacion || "-")}
                    </CardTitle>
                  </div>
                </Col>
              </Row>
            </CardBody>
            <div className="card-footer">
              <hr />
              <div className="stats">
                <i className="tim-icons icon-calendar-60" /> Ciclo activo
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Tabla de últimos alumnos */}
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Últimos Alumnos Matriculados</CardTitle>
              <p className="card-category">
                {cicloActual?.denominacion || "Ciclo actual"}
              </p>
            </CardHeader>
            <CardBody>
              <Table responsive>
                <thead className="text-primary">
                  <tr>
                    <th>DNI</th>
                    <th>Apellidos y Nombres</th>
                    <th>Salón</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="text-center">Cargando...</td>
                    </tr>
                  ) : ultimosAlumnos.length > 0 ? (
                    ultimosAlumnos.map((m, i) => {
                      const a = m.alumno || m;
                      return (
                        <tr key={m._id || i}>
                          <td>{a.documentoidentidad}</td>
                          <td>
                            {a.apellidopaterno} {a.apellidomaterno}, {a.nombres}
                          </td>
                          <td>{m.salon?.denominacion || m.salon || "-"}</td>
                          <td>
                            {m.estado === "1" || m.estado === "Activo" ? (
                              <span className="text-success">Activo</span>
                            ) : (
                              <span className="text-danger">Inactivo</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center">
                        {cicloActual ? "No hay alumnos en el ciclo actual" : "Sin ciclo activo"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default Dashboard;
